/**
 * API Utilities for cross-device compatibility
 * Handles proper URL resolution whether accessed from localhost or external devices
 */

/**
 * Get the base URL for API requests
 * @returns {string} The base URL to use for API requests
 */
export const getApiBaseUrl = () => {
  // Check if we have an environment variable set for the server URL
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  
  // If running on localhost, we can use relative URLs (they'll be handled by the proxy)
  // Otherwise use the full server URL from environment variables
  const isLocalhost = 
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1';
  
  if (isLocalhost && !serverUrl) {
    // When on localhost without explicit server URL, use relative paths (proxy handles it)
    return '';
  }
  
  // When accessed from external devices or when server URL is explicitly set,
  // use the full server URL from environment variable
  return serverUrl || '';
};

/**
 * Create a full API URL
 * @param {string} endpoint - The API endpoint (e.g., '/api/users')
 * @returns {string} The full API URL
 */
export const createApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  
  // If baseUrl is empty, we're using relative URLs
  if (!baseUrl) {
    return endpoint;
  }
  
  // Otherwise, construct a full URL
  // Make sure the baseUrl doesn't end with a slash and endpoint starts with one
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Performs a fetch request with proper URL resolution and authentication
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const apiFetch = async (endpoint, options = {}) => {
  // Get the token from local storage
  const token = localStorage.getItem('token') || 'dummy-token';
  
  // Create the URL
  const url = createApiUrl(endpoint);
  
  // Set up headers with authentication
  const isFormData = options.body instanceof FormData;
  
  // Only set Content-Type to application/json if not FormData
  // For FormData, let the browser set the appropriate Content-Type with boundary
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    'Authorization': `Bearer ${token}`,
    ...(options.headers || {})
  };
  
  // Log the request details in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`🌐 API Request to: ${url}`);
  }
  
  // Perform the fetch with the resolved URL and headers
  try {
    // Log request details, handling FormData specially
    console.log(`🔍 DEBUG: Sending API request to ${url}`, {
      method: options.method || 'GET',
      headers,
      bodyType: options.body ? (isFormData ? 'FormData' : typeof options.body) : 'none',
      bodyLength: options.body ? (isFormData ? 'FormData object' : options.body.length) : 0,
      body: options.body && !isFormData && typeof options.body === 'string' ? JSON.parse(options.body) : null
    });
    
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    // Log the response status for debugging
    console.log(`🔍 DEBUG: API response from ${url}:`, {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      contentType: response.headers.get('content-type')
    });
    
    return response;
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    throw error;
  }
};

/**
 * Get WebSocket URL for connections
 * @param {string} endpoint - Optional endpoint (e.g., '/ws')
 * @returns {string} The WebSocket URL to connect to
 */
export const getWebSocketUrl = (endpoint = '/ws') => {
  // Get the same base server URL used for HTTP requests
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  
  // Determine if we're using HTTPS protocol
  const isHttps = window.location.protocol === 'https:';
  
  // Define the WebSocket protocol based on HTTP protocol
  const wsProtocol = isHttps ? 'wss:' : 'ws:';
  
  // Check if we're on localhost
  const isLocalhost = 
    window.location.hostname === 'localhost' || 
    window.location.hostname === '127.0.0.1';
    
  // Determine if we're in production (needed for special handling of Render deployment)
  const isProduction = process.env.NODE_ENV === 'production';
  
  // Get the token for authentication
  const token = localStorage.getItem('token') || '';
  
  // Ensure endpoint starts with a slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  let wsUrl;
  
  if (isLocalhost && !serverUrl) {
    // When on localhost without explicit server URL, use localhost:3002 directly
    wsUrl = `${wsProtocol}//localhost:3002${normalizedEndpoint}`;
  } else if (serverUrl) {
    // When server URL is specified, extract the host and port
    try {
      const url = new URL(serverUrl);
      
      // In production (Render), don't specify port to use default HTTPS port (443)
      // which is handled by Render's SSL termination
      if (isProduction && isHttps) {
        wsUrl = `${wsProtocol}//${url.hostname}${normalizedEndpoint}`;
      } else {
        wsUrl = `${wsProtocol}//${url.hostname}:${url.port || '3002'}${normalizedEndpoint}`;
      }
    } catch (e) {
      // Fallback if URL parsing fails
      if (isProduction && isHttps) {
        // In production (Render), don't specify port
        wsUrl = `${wsProtocol}//${window.location.hostname}${normalizedEndpoint}`;
      } else {
        wsUrl = `${wsProtocol}//${window.location.hostname}:3002${normalizedEndpoint}`;
      }
    }
  } else {
    // Default case
    if (isProduction && isHttps) {
      // In production (Render), don't specify port
      wsUrl = `${wsProtocol}//${window.location.hostname}${normalizedEndpoint}`;
    } else {
      // Otherwise use port 3002
      wsUrl = `${wsProtocol}//${window.location.hostname}:3002${normalizedEndpoint}`;
    }
  }
  
  // Add token if available
  if (token) {
    wsUrl += `?token=${encodeURIComponent(token)}`;
  }
  
  console.log(`Generated WebSocket URL: ${wsUrl} (isProduction: ${isProduction}, isHttps: ${isHttps})`);
  
  return wsUrl;
};

/**
 * Specialized fetch function for notification endpoints
 * Uses the same URL resolution logic as regular API calls to ensure
 * it works correctly in both development and production environments
 * @param {string} endpoint - The API endpoint (should start with '/api/notifications/')
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const apiNotificationFetch = async (endpoint, options = {}) => {
  // Get the token from local storage
  const token = localStorage.getItem('token') || 'dummy-token';
  
  // Ensure endpoint starts with correct path
  if (!endpoint.startsWith('/api/notifications/')) {
    console.warn('Non-notification endpoint called with notification fetch');
  }
  
  // Use the same URL creation logic as regular API calls
  const url = createApiUrl(endpoint);
  
  // Set up headers with authentication
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...(options.headers || {})
  };
  
  console.log(`🔔 NOTIFICATION API: Sending request to ${url}`);
  
  // Perform the fetch with the resolved URL
  try {
    console.log(`🔍 DEBUG: Sending notification API request to ${url}`, {
      method: options.method || 'GET',
      bodyLength: options.body ? options.body.length : 0,
      body: options.body ? JSON.parse(options.body) : null
    });
    
    const response = await fetch(url, {
      ...options,
      headers
    });
    
    // Log the response status for debugging
    console.log(`🔍 DEBUG: Notification API response from ${url}:`, {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });
    
    return response;
  } catch (error) {
    console.error(`Error fetching notification endpoint ${endpoint}:`, error);
    throw error;
  }
};